import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Grid,
  Text,
  Button,
  Card,
  IconButton,
  Icons,
  DialogModal,
  Drawer,
} from "@pushpress/shared-components";

import SetupItem from "./SetupItem";
import SetupRulesPane from "./SetupRulesPane";
import SetupNamePane from "./SetupNamePane";
import { useAppointmentTypesQuery } from "../../apis/appointmentApi.hooks";
import { useGetCalendarClassTypesSelectQuery } from "../../components/controllers/CalendarItemTypeSelect/CalendarItemTypeSelect-generated.hooks";
import { sortByName } from "../../utils/sortUtils";
import { useNameSuggestionsQuery } from "./committedClubApi.hooks";
import {
  BadgesService,
  BadgeTemplate,
  CompanyService,
} from "@pushpress/committed-club-typescript-sdk";
import { Helmet } from "react-helmet";
import Dashboard from "../../layouts/Dashboard";
import styles from "./CommittedClub.module.scss";
import { useHistory } from "react-router-dom";
import { ClubCheckinRules, UpdateDataBody } from "./types";
import {
  appointmentCheckinOptions,
  areArraysEqual,
  classCheckinOptions,
  eventCheckinOptions,
  getSetupItemTitleFromTemplate,
} from "./utils";
import { CommittedClubEvent } from "./CommittedClubEvents";
import { useAnalytics } from "../../components/providers/AnalyticsProvider";
import DrawerPanel from "../../components/common/DrawerPanel/DrawerPanel";

type SetupView = undefined | "name" | "rules";

type CommittedClubSettingsProps = {
  badgeTemplate: BadgeTemplate;
  refetchBadgeTemplate: () => Promise<void>;
};

const CommittedClubSettings = (props: CommittedClubSettingsProps) => {
  const { badgeTemplate, refetchBadgeTemplate } = props;

  const [thisMonthBadgeTemplate, setThisMonthBadgeTemplate] =
    useState<BadgeTemplate>();
  const [sheetView, setSheetView] = useState<SetupView>();
  const [name, setName] = useState<string>();
  const [rules, setRules] = useState<ClubCheckinRules>();
  const [showDialog, setShowDialog] = useState(false);
  const [deactivating, setDeactivating] = useState(false);

  const [hasNewRules, setHasNewRules] = useState(false);

  const history = useHistory();

  const { trackEvent } = useAnalytics();

  useEffect(() => {
    BadgesService.getActiveBadge().then((response) => {
      setThisMonthBadgeTemplate(response.data);
    });
  }, []);

  useEffect(() => {
    if (!badgeTemplate) {
      return;
    }

    const seedStateFromBadgeTemplate = () => {
      setName(badgeTemplate.title);

      const {
        appointmentSelectionStrategy,
        classSelectionStrategy,
        includeEvents,
        includeOpenGym,
        selectedAppointmentTypeIds,
        selectedClassIds,
      } = badgeTemplate.criteria;

      const eventTypes =
        includeEvents && includeOpenGym
          ? "BOTH"
          : includeEvents
          ? "EVENTS"
          : includeOpenGym
          ? "OPENGYMS"
          : "NONE";

      setRules({
        checkinCount: badgeTemplate.criteriaTotal,
        checkinTypes: selectedClassIds,
        appointmentTypes: selectedAppointmentTypeIds,
        checkinTypeLevel: classCheckinOptions.find(
          (o) => o.value === classSelectionStrategy,
        ),
        appointmentTypeLevel: appointmentCheckinOptions.find(
          (o) => o.value === appointmentSelectionStrategy,
        ),
        eventTypes: eventCheckinOptions.find((o) => o.value === eventTypes),
      });
    };

    seedStateFromBadgeTemplate();
  }, [badgeTemplate]);

  useEffect(() => {
    const current = thisMonthBadgeTemplate;
    const upcoming = badgeTemplate;

    if (!current || !upcoming) {
      return;
    }

    const hasDifferences =
      current.criteriaTotal !== upcoming.criteriaTotal ||
      current.criteria.appointmentSelectionStrategy !==
        upcoming.criteria.appointmentSelectionStrategy ||
      current.criteria.classSelectionStrategy !==
        upcoming.criteria.classSelectionStrategy ||
      current.criteria.includeEvents !== upcoming.criteria.includeEvents ||
      current.criteria.includeOpenGym !== upcoming.criteria.includeOpenGym ||
      (current.criteria.appointmentSelectionStrategy === "SPECIFIC" &&
        !areArraysEqual(
          current.criteria.selectedAppointmentTypeIds,
          upcoming.criteria.selectedAppointmentTypeIds,
        )) ||
      (current.criteria.classSelectionStrategy === "SPECIFIC" &&
        !areArraysEqual(
          current.criteria.selectedClassIds,
          upcoming.criteria.selectedClassIds,
        ));

    setHasNewRules(hasDifferences);
  }, [badgeTemplate, thisMonthBadgeTemplate]);

  const closeDrawer = useCallback(
    (trackClose = true) => {
      if (trackClose) {
        trackEvent(CommittedClubEvent.Closed, {
          state: "launched",
          app: "committed_club",
          page: `${sheetView}_panel`,
        });
      }
      setSheetView(undefined);
    },
    [sheetView, trackEvent],
  );

  const goBack = useCallback(() => {
    history.length > 0 ? history.goBack() : history.push("/committed-club");
  }, [history]);

  const aiNameSuggestions = useNameSuggestionsQuery();

  const { data: classTypes } = useGetCalendarClassTypesSelectQuery({
    fetchPolicy: "no-cache",
    notifyOnNetworkStatusChange: true,
  });

  const classTypesFiltered = useMemo(() => {
    const calendarTypes = classTypes?.getCalendarClassTypes || [];

    const classTypeOptions = calendarTypes
      .filter(
        (calendarType) => calendarType.isActive && !calendarType.isDeleted,
      )
      .sort(sortByName)
      .map((calendarType) => ({
        label: calendarType.name,
        value: calendarType.uuid,
      }));

    return classTypeOptions;
  }, [classTypes]);

  const { data: appointmentTypesResponse } = useAppointmentTypesQuery();
  const appointmentTypesFiltered = useMemo(() => {
    const appointmentTypes = appointmentTypesResponse?.data ?? [];

    const appointmentTypeOptions = appointmentTypes
      .sort(sortByName)
      .map((appointmentType) => ({
        label: appointmentType.name,
        value: appointmentType.uuid,
      }));

    return appointmentTypeOptions;
  }, [appointmentTypesResponse]);

  const updateClub = useCallback(
    async (update: Partial<UpdateDataBody>) => {
      await BadgesService.update({ body: { ...badgeTemplate, ...update } });
      refetchBadgeTemplate();
    },
    [badgeTemplate, refetchBadgeTemplate],
  );

  const showDeactivationWarning = useCallback(() => setShowDialog(true), []);

  const abortDeactivation = useCallback(() => setShowDialog(false), []);

  const deactivateClub = useCallback(async () => {
    setShowDialog(false);
    setDeactivating(true);
    await CompanyService.deactivateApp();
    await refetchBadgeTemplate();
    history.push("/committed-club");
  }, [history, refetchBadgeTemplate]);

  const showNamePane = useCallback(() => {
    trackEvent(CommittedClubEvent.NameSelected, {
      state: "launched",
      app: "committed_club",
      page: "committed_club_settings",
    });
    setSheetView("name");
  }, [trackEvent]);

  const showRulesPane = useCallback(() => {
    trackEvent(CommittedClubEvent.RulesSelected, {
      state: "launched",
      app: "committed_club",
      page: "committed_club_settings",
    });
    setSheetView("rules");
  }, [trackEvent]);

  const drawerTitle =
    sheetView === "name"
      ? "Choose a name"
      : sheetView === "rules"
      ? "Edit rules"
      : "";

  const drawer = (
    <Drawer visible={!!sheetView} anchor="right" width="400px">
      <DrawerPanel title={drawerTitle} onClose={closeDrawer}>
        <div className="tw-px-4 md:tw-px-0">
          {sheetView === "name" && (
            <SetupNamePane
              isClubSetup
              hasLaunched
              value={name}
              setName={setName}
              suggestionsHook={aiNameSuggestions}
              updateClub={updateClub}
              closeDrawer={closeDrawer}
            />
          )}

          {sheetView === "rules" && (
            <SetupRulesPane
              isClubSetup
              hasLaunched
              value={rules}
              setRules={setRules}
              classTypes={classTypesFiltered}
              updateClub={updateClub}
              appointmentTypes={appointmentTypesFiltered}
              closeDrawer={closeDrawer}
            />
          )}
        </div>
      </DrawerPanel>
    </Drawer>
  );

  const content = (
    <Grid item xs={12} alignItems="flex-start">
      <Card>
        <Card.Content>
          <div className="tw-py-4">
            <Text weight="600" variant="heading-4" color="black">
              {"Settings"}
            </Text>

            <div className="tw-h-6" />

            <SetupItem
              title="Name"
              instructionTitle="Name your Committed Club"
              value={[name]}
              buttonText="Choose a name"
              onClick={showNamePane}
            />

            <div className="tw-h-4" />

            <SetupItem
              title="Rules"
              disabled={hasNewRules}
              instructionTitle="Set rules for your Committed Club"
              value={getSetupItemTitleFromTemplate(thisMonthBadgeTemplate)}
              buttonText="Choose rules"
              onClick={!hasNewRules && showRulesPane}
            />

            {hasNewRules && (
              <>
                <div className="tw-h-4" />

                <SetupItem
                  title="Next Month's Rules"
                  instructionTitle="Set rules for your Committed Club"
                  value={getSetupItemTitleFromTemplate(props.badgeTemplate)}
                  buttonText="Choose rules"
                  onClick={showRulesPane}
                />
              </>
            )}

            <div className="tw-h-8" />

            <div className="tw-w-full sm:tw-w-1/3">
              <Button
                fullWidth
                loading={deactivating}
                text={"Deactivate"}
                disabled={deactivating}
                buttonType="destructive"
                size="large"
                onClick={showDeactivationWarning}
              />
            </div>

            <div className="tw-h-3" />

            <Text variant="body-lg" color="wolf-600">
              Once deactivated you won’t have access to Committed Club results
              so please export any you need before deactivating.
            </Text>
          </div>
        </Card.Content>
      </Card>
    </Grid>
  );

  const warningDialog = (
    <DialogModal
      visible={showDialog}
      title="Are you sure you want to deactivate your Committed Club?"
      primaryBtnTitle="Deactivate"
      primaryType="destructive"
      primaryButtonOptions={{ fullWidth: true }}
      primaryHandler={deactivateClub}
      secondaryBtnTitle="Go Back"
      secondaryHandler={abortDeactivation}
    >
      <p>
        Once your Committed Club is deactivated, it will no longer be available
        for your members to participate, and you will not be able to access past
        data.
      </p>
    </DialogModal>
  );

  return (
    <>
      <Helmet>
        <title>Committed Club</title>
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no"
        />
      </Helmet>
      <Dashboard scrollableContent disableContentPadding>
        {drawer}
        {warningDialog}
        <div className={styles.container}>
          <div className="tw-flex tw-items-center tw-space-x-4">
            <IconButton
              active
              icon={Icons.ArrowLeft}
              onClick={goBack}
              size={24}
            />
            <Text weight="600" variant="heading-3" color="black">
              Committed Club
            </Text>
          </div>
          <div className="tw-h-1" />
          {content}
        </div>
      </Dashboard>
    </>
  );
};

export default CommittedClubSettings;
